/**
 * The CCS Approach — premium homepage intro sequence.
 * Hand-crafted SVG interpretation of the brand emblem: a gold ring draws in,
 * three stylized human figures (head + raised open arms, converging in unity)
 * rise one after another — each revealing one pillar — then the figures
 * dissolve into the completed CCS logo before the homepage fades into view.
 */
import { useEffect } from "react";
import { motion } from "framer-motion";
import { EmblemImage } from "@/components/brand/Logo";

const EASE = [0.22, 1, 0.36, 1];

// Stylized human form: petal body whose two lobes are raised, open arms,
// tapering to a single point at the base — abstract, timeless, connected.
const FIGURE_BODY =
  "M0,0 C-9,-22 -22,-38 -26,-60 C-29,-76 -14,-82 -9,-68 C-6,-59 -3,-50 0,-42 C3,-50 6,-59 9,-68 C14,-82 29,-76 26,-60 C22,-38 9,-22 0,0 Z";

const Figure = ({ color, delay, animated = true }) => (
  <motion.g
    initial={animated ? { opacity: 0, y: 18 } : false}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.65, ease: EASE }}
  >
    <path d={FIGURE_BODY} fill={color} />
    <circle cx="0" cy="-92" r="13" fill={color} />
  </motion.g>
);

/** The recreated CCS emblem. The three figures fan outward from a shared
 * base — heads apart, feet together — echoing the unity of the real logo. */
export const CCSEmblem = ({ animated = true, className = "", testid = "ccs-emblem" }) => (
  <motion.svg
    viewBox="0 0 240 240"
    className={className}
    data-testid={testid}
    initial={animated ? { opacity: 0, scale: 0.94 } : false}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6, ease: EASE }}
    role="img"
    aria-label="The CCS Approach emblem"
  >
    <defs>
      <linearGradient id="ccsGold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#E7B54D" />
        <stop offset="55%" stopColor="#C28A2E" />
        <stop offset="100%" stopColor="#9A6B1E" />
      </linearGradient>
    </defs>

    {/* Cream backdrop disc */}
    <motion.circle
      cx="120" cy="120" r="108" fill="#FFFDF7"
      initial={animated ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.25, duration: 0.5 }}
    />

    {/* Gold ring — draws itself from the top */}
    <motion.circle
      cx="120" cy="120" r="105"
      fill="none" stroke="url(#ccsGold)" strokeWidth="7" strokeLinecap="round"
      transform="rotate(-90 120 120)"
      initial={animated ? { pathLength: 0, opacity: 0 } : false}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.9, ease: "easeInOut" }}
    />

    {/* Three figures — bases converge in the centre, heads fan outward.
        Left (gold) → Communicate · Centre (rust) → Collaborate · Right (mahogany) → Serve */}
    <g transform="translate(100,177) rotate(-24)">
      <Figure color="#DFA036" delay={1.0} animated={animated} />
    </g>
    <g transform="translate(140,177) rotate(24)">
      <Figure color="#5D3A1F" delay={2.0} animated={animated} />
    </g>
    <g transform="translate(120,174) scale(1.22)">
      <Figure color="#B7541E" delay={1.5} animated={animated} />
    </g>

    {/* C · C · S letters */}
    <motion.text
      x="120" y="209" textAnchor="middle"
      fontFamily="Georgia, 'Times New Roman', serif" fontSize="16" fontWeight="700"
      letterSpacing="3" fill="#4A2E18"
      initial={animated ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={{ delay: 2.35, duration: 0.5 }}
    >
      C · C · S
    </motion.text>
  </motion.svg>
);

const PILLARS = [
  { word: "Communicate", color: "#E7B54D", delay: 1.0 },
  { word: "Collaborate", color: "#D2703A", delay: 1.5 },
  { word: "Serve", color: "#C9A56A", delay: 2.0 },
];

// Timeline: ring 0.3–1.2s · figures 1.0/1.5/2.0s · letters 2.35s ·
// crossfade to real logo 2.8–3.5s · homepage fades in at 3.9s
const CROSSFADE_AT = 2.8;
const DONE_AT_MS = 3900;

export const IntroAnimation = ({ onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, DONE_AT_MS);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      data-testid="intro-overlay"
      className="fixed inset-0 z-50 bg-[#17100B] flex flex-col items-center justify-center px-6"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.9, ease: "easeInOut" } }}
    >
      <motion.div
        className="flex flex-col items-center"
        exit={{ opacity: 0, y: -16, transition: { duration: 0.6, ease: EASE } }}
      >
        {/* Emblem: SVG figures dissolve into the completed CCS logo */}
        <div className="relative w-52 h-52 sm:w-64 sm:h-64">
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ delay: CROSSFADE_AT, duration: 0.7, ease: "easeInOut" }}
          >
            <CCSEmblem animated className="w-full h-full" testid="intro-emblem" />
          </motion.div>
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: CROSSFADE_AT, duration: 0.7, ease: "easeInOut" }}
            data-testid="intro-real-logo"
          >
            <EmblemImage className="w-full h-full" testid="intro-emblem-image" />
          </motion.div>
        </div>

        {/* Pillar words */}
        <div
          className="mt-10 flex items-center gap-3 sm:gap-5 flex-wrap justify-center"
          data-testid="intro-pillars"
        >
          {PILLARS.map((p, i) => (
            <div key={p.word} className="flex items-center gap-3 sm:gap-5">
              {i > 0 && (
                <motion.span
                  className="w-1.5 h-1.5 rounded-full bg-[#C28A2E]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: p.delay + 0.15, duration: 0.4 }}
                />
              )}
              <motion.span
                className="font-display text-lg sm:text-2xl font-light tracking-[0.18em] uppercase"
                style={{ color: p.color }}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: p.delay, duration: 0.6, ease: EASE }}
              >
                {p.word}
              </motion.span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Skip affordance */}
      <motion.button
        type="button"
        data-testid="intro-skip-btn"
        onClick={onDone}
        className="fixed bottom-6 right-6 text-[11px] uppercase tracking-[0.2em] text-[#F0E6D2] opacity-40 hover:opacity-100 transition-opacity duration-300 font-medium"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        whileHover={{ opacity: 1 }}
      >
        Skip intro →
      </motion.button>
    </motion.div>
  );
};

export default IntroAnimation;
